import { Controller } from "@hotwired/stimulus"

import { dayTopUp, foldChainBreaks } from "../utils/day_break_top_up"
import { setSubmitGate, submitGated } from "../utils/submit_gate"
import { entryBlocks, entryNetMinutes, minutesToHHMM, toMinutes } from "../utils/timesheet_net_time"

// Mirrors the statutory break rule (WorkTimeModel#break_rules) while the day is
// being entered, so the employee sees the required break before the save instead
// of discovering it afterwards — either as a silently corrected value or as a
// conflict badge on a row they already submitted.
//
// This controller deliberately holds NO policy. The save has five separate
// reasons not to top a shortfall up itself (a fleet entry, a recorded check-in,
// a carved multi-site slice, periods whose author owns them, mode "validate"),
// and a controller re-deriving those from the DOM gets them wrong — it blocked
// days the server would have saved and saved days the server would have refused.
// ConfigurableTimeSheetsHelper#work_time_break_rule resolves the verdict where
// the template, the entry and the actor are all known, and hands over four
// flags. What is left here is arithmetic on the entered window.
//
//   serverFixes     the save tops a shortfall up by itself
//   enforced        a surviving shortfall can stop the save at all. False on a
//                   multi-site day (carved into slices whose break error is
//                   dropped), a fleet entry and a check-in-recorded one
//   scalarWritable  time_break is a field this form may write. False whenever
//                   von/bis periods are on: the total is derived from their sum
//                   and owned by break_periods_total_controller, and two
//                   controllers writing one input is an event loop, not a race
//   authorOwnsPeriods  whoever is filling in this form can edit the von/bis rows,
//                   so only they can say WHEN the missing pause was taken
//   atLeast         a day landing exactly on a threshold already crosses it
//                   ("ab 9 Std" rather than the statutory "ab mehr als 9 Std")
//   lowerToMinimum  the save takes its own earlier top-up back when the day
//                   shrinks again, so the form does the same instead of leaving
//                   a Pause on screen that the save is about to shorten
//   lowerPrefilledBreak / prefilledMinutes
//                   the Pause this form opened with came from the work-time
//                   model, not from a person (#break_rule_prefill), so while it
//                   is untouched it follows the entered window rather than
//                   standing. Without it a day shortened to 11:00-11:30 keeps
//                   the whole pre-filled 00:30 and books as 0,00 h. Unlike
//                   lowerToMinimum this never applies to a saved entry: the
//                   server sends 0 for one, because that value has an author
//   manualMinutes   the break minutes the entry's author put there themselves.
//                   Everything is measured from this baseline rather than from
//                   the Pause on screen, which may already carry a top-up — see
//                   ConfigurableTimeSheet#manual_break_minutes for why
//                   re-deriving from a raised break does not converge
//
// The values above describe the form's ONE rule. A form whose rows span several
// days can hold more than one — the Schnellerfassung's week mode renders a row
// per day, and a work-time model boundary can fall between two of them — so a row
// may carry the day-specific half of its own rule as JSON in
// data-break-rule-overrides, or opt out of the rule entirely with
// data-break-rule-skip. See #ruleFor and #skipEntry.
//
// Same contract as work_time_limit_controller otherwise: the server validates
// regardless, and this checks only the entered window(s).
export default class extends Controller {
  static values = {
    mode: String,
    action: String,
    authorOwnsPeriods: Boolean,
    serverFixes: Boolean,
    enforced: Boolean,
    scalarWritable: Boolean,
    atLeast: Boolean,
    lowerToMinimum: Boolean,
    lowerPrefilledBreak: Boolean,
    prefilledMinutes: Number,
    manualMinutes: Number,
    thresholds: Array,
    dayTopUp: Boolean,
    dayTopUpPeriods: Boolean,
    dayTopUpGapMax: { type: Number, default: -1 },
    dayTopUpPersisted: Boolean,
    daySiblings: Array,
  }

  connect() {
    this._form = this.element.closest("form") || this.element
    // Per entry: the correction this controller last made and the Pause it wrote
    // for it. Writing that Pause dispatches a change, which re-enters sync() with
    // the shortfall already gone — without this the note is cleared on the same
    // tick it appears, and the correction is silent again, which is the one thing
    // this controller exists to prevent. It applies to BOTH directions: a lowering
    // is just as invisible as a raise if nothing says it happened.
    this._correctedTo = new Map()
    // Per entry: the author's own break minutes, and the last total this
    // controller wrote. Together they mirror the provenance the save keeps in
    // metadata — a Pause the author types over becomes the new baseline, one we
    // wrote does not.
    this._baseline = new Map()
    this._lastWritten = new Map()
    // Per entry: whether the Pause is still the one the server pre-filled. Only a
    // block the SERVER rendered can start out true — one multi_entry_controller
    // cloned carries whatever was on screen when it was cloned, which is a
    // person's; the Schnellerfassung's rows say so each for themselves.
    this._prefillIntact = new Map()

    // break_periods_total_controller recomputes time_break and dispatches
    // synthetic input/change events, and multi_entry_controller clones whole
    // entry blocks — listening on the form catches both.
    this._handleChange = (event) => {
      const name = event.target?.name || ""
      if (name.includes("time_from") || name.includes("time_to") || name.includes("time_break")) {
        this.sync()
      }
    }
    // A disabled button is not the only way a form can be submitted.
    this._handleSubmit = (event) => {
      if (submitGated(this._form)) event.preventDefault()
    }
    this._form.addEventListener("change", this._handleChange)
    this._form.addEventListener("input", this._handleChange)
    this._form.addEventListener("submit", this._handleSubmit)
    // multi_entry_controller writes chained start times and Pause-Blocks without
    // input events of their own.
    this._handleChained = () => this.sync()
    this._form.addEventListener("multi-entry:chained", this._handleChained)

    // Let the time-input controllers write their hidden fields first.
    setTimeout(() => this.sync(), 150)
  }

  disconnect() {
    this._form.removeEventListener("change", this._handleChange)
    this._form.removeEventListener("input", this._handleChange)
    this._form.removeEventListener("submit", this._handleSubmit)
    this._form.removeEventListener("multi-entry:chained", this._handleChained)
    // Release only this controller's own gate — multi_site_booking may still be
    // holding its own.
    setSubmitGate(this._form, "break-rule", false)
  }

  // Break rules are evaluated PER ENTRY on the server, so every block in the form
  // is judged on its own: the base `configurable_time_sheet[...]` one and each
  // `additional_entries[N][...]` clone multi_entry_controller made. Their hints
  // and their break-period rows live inside the entry, not the form — see
  // entryBlocks.
  sync() {
    let blocked = false
    if (this.syncDay()) return

    entryBlocks(this._form).forEach(({ prefix, element, values }) => {
      // Rows the rule has nothing to say about: a day the Schnellerfassung is
      // carrying along unticked (the batch creator drops it), or one whose own
      // day resolves to no rule at all. Judged anyway, an unticked Friday raises
      // a Pause nobody is booking and — under "validate"/"block" — holds the
      // whole week's submission hostage to a shortfall on a day that will never
      // be created.
      if (this.skipEntry(element)) {
        this.renderHints(element, "ok", 0)
        return
      }

      const rule = this.ruleFor(element)
      const entered = toMinutes(values.time_break)
      const manual = this.baselineFor(prefix, entered, element)
      this.declareBaseline(prefix, manual)

      // A half-entered window has no requirement to mirror. entryNetMinutes
      // reports 0 net for one (it cannot do better), which reads here as a day
      // so short that nothing is required — and with lowerToMinimum that is a
      // live instruction to cut the Pause back to the baseline, mid-keystroke,
      // while the employee is still retyping the end time. Wait for both ends.
      if (!values.time_from || !values.time_to) {
        this.renderHints(element, "ok", 0)
        return
      }

      // Gross window minus the author's own break: the entered state, which is
      // what the requirement is derived from on both sides.
      const net = entryNetMinutes({ ...values, time_break: null }) - manual
      const required = this.requiredBreakMinutes(net, rule)
      const ownRaise = this.ownUnsavedRaise(prefix, entered)
      const lowering = this.canLower(element, prefix, rule) || ownRaise
      const target = this.targetBreakMinutes(manual, entered, required, lowering)
      const shortfall = Math.max(target - entered, 0)
      let state = this.entryState(prefix, element, target, entered, shortfall, required, lowering)
      if (ownRaise && state === "lowered") state = target > manual ? "raised" : "reverted"

      if (state === "reverted") {
        // Our own raise, never saved, for a window that no longer needs it — the
        // Pause goes back to what the author entered, and there is nothing to say.
        this._correctedTo.delete(prefix)
        this.writeBreak(prefix, target)
        state = "ok"
      } else if (state === "raised" || state === "lowered") {
        // Recorded BEFORE the write, so the sync our own change event triggers
        // already knows this Pause is ours and keeps the note up.
        this._correctedTo.set(prefix, { kind: state, minutes: target })
        this.writeBreak(prefix, target)
      } else if (state !== "ok") {
        this._correctedTo.delete(prefix)
      }
      if (state === "shortfall") blocked = blocked || this.blocks(element, rule)

      this.renderHints(element, state, this.hintMinutes(prefix, state, target, shortfall))
    })

    setSubmitGate(this._form, "break-rule", blocked)
  }

  // "Pause nach Arbeitszeitmodell automatisch ergänzen": once the day holds two or
  // more blocks the save judges them as ONE day and places the missing pause
  // itself (ConfigurableTimeSheets::MultiEntryBreakTopUp) — no block is corrected
  // on its own, nothing blocks the save, and the day's top-up is announced
  // instead. The day is the form's blocks plus, on an edit, the ones already saved
  // for it (daySiblings): the save counts those too. Returns false while the day
  // holds a single block, which the per-entry rule keeps judging as before.
  syncDay() {
    const notice = this._form.querySelector("[data-day-break-notice]")
    const siblings = this.dayTopUpValue ? this.savedDayBlocks() : []
    const blocks = this.dayTopUpValue ? this.multiEntryBlocks(siblings.length > 0) : []
    if (blocks.length + siblings.length < 2) {
      notice?.classList.add("hidden")
      return false
    }

    blocks.forEach(({ element }) => this.renderHints(element, "ok", 0))
    setSubmitGate(this._form, "break-rule", false)

    const folded = foldChainBreaks(
      blocks.map((block) => block.day),
      this.chainBreaks(),
      this.dayTopUpPeriodsValue,
    )
    const result = dayTopUp({
      blocks: [...folded, ...siblings],
      thresholds: this.thresholdsValue,
      atLeast: this.atLeastValue,
      gapBreakMax: this.dayTopUpGapMaxValue < 0 ? undefined : this.dayTopUpGapMaxValue,
      periodsMode: this.dayTopUpPeriodsValue,
    })
    this.renderDayNotice(notice, result, blocks, siblings)
    return true
  }

  // The day's blocks saved by earlier submissions, as the server measures them
  // (MultiEntryBreakTopUp.sibling_blocks_for). Only a pending, non-recorded one
  // can take a share (+mutable+).
  savedDayBlocks() {
    return (this.daySiblingsValue || []).map((sibling) => ({
      key: sibling.key,
      from: sibling.from,
      to: sibling.to,
      periods: sibling.periods || [],
      ownBreak: sibling.own_break || 0,
      mutable: sibling.mutable !== false,
    }))
  }

  // The form's entry blocks as the day top-up sees them, in form order: window,
  // von/bis rows and the Pause each block already carries. +includeBase+ takes the
  // base entry in on a form without a multi-entry wrapper (the admin edit), whose
  // one block only forms a day together with the saved ones.
  //
  // What counts as already taken mirrors the save: a row the rule generated
  // earlier is re-derived, not counted, and a saved block's scalar Pause counts
  // only its author's part — the baseline, not a total that includes the rule's.
  multiEntryBlocks(includeBase = false) {
    return entryBlocks(this._form)
      .filter(
        ({ element }) =>
          (includeBase && element === this._form) ||
          (element !== this._form && element.matches('[data-multi-entry-target="entry"]')),
      )
      .map(({ prefix, element, values }) => {
        const from = values.time_from ? toMinutes(values.time_from) : 0
        let to = values.time_to ? toMinutes(values.time_to) : 0
        if (values.time_to && to === 0) to = 1440
        const periods = this.liveBreakPeriodRows(element)
          .filter((row) => !row.hasAttribute("data-break-period-rule-generated"))
          .map((row) => [
            toMinutes(row.querySelector("input[name$='[break_from]']")?.value),
            toMinutes(row.querySelector("input[name$='[break_to]']")?.value),
          ])
          .filter(([start, end]) => end > start)
        const entered = toMinutes(values.time_break)
        const savedBase = this.dayTopUpPersistedValue && prefix === this.basePrefix()
        let ownBreak = entered
        if (this.dayTopUpPeriodsValue) {
          ownBreak = periods.reduce((sum, [start, end]) => sum + end - start, 0)
        } else if (savedBase) {
          ownBreak = this.baselineFor(prefix, entered, element)
        }
        const day = {
          key: prefix,
          from,
          to: values.time_from && values.time_to ? to : from,
          periods,
          ownBreak,
        }
        return { prefix, element, day }
      })
  }

  // The Pause-Blocks multi_entry_controller keeps between chained blocks.
  chainBreaks() {
    return [...this._form.querySelectorAll("[data-multi-entry-break-row]")]
      .map((row) => ({
        afterKey: row.dataset.afterPrefix,
        from: Number(row.dataset.from),
        to: Number(row.dataset.to),
      }))
      .filter((pause) => pause.afterKey && pause.to > pause.from)
  }

  renderDayNotice(notice, result, blocks, siblings = []) {
    if (!notice) return
    if (!result || result.shortfall === 0) {
      notice.classList.add("hidden")
      return
    }

    const label = notice.dataset.entryLabel || "Eintrag"
    // A saved block is named by its window — it has no number in this form.
    const savedLabel = (key) => {
      const sibling = siblings.find((block) => block.key === key)
      if (!sibling) return null

      const window = `${minutesToHHMM(sibling.from)}–${minutesToHHMM(sibling.to)}`
      return (notice.dataset.savedEntryLabel || "%{window}").replace("%{window}", window)
    }
    const name = (key) =>
      savedLabel(key) ?? `${label} ${blocks.findIndex((block) => block.prefix === key) + 1}`
    const placed = result.placements.reduce((sum, [, from, to]) => sum + to - from, 0)
    let text
    if (placed === 0) {
      text = notice.dataset.textUnplaceable.replace("%{minutes}", String(result.shortfall))
    } else if (this.dayTopUpPeriodsValue) {
      const places = result.placements
        .map(([key, from, to]) => `${name(key)}, ${minutesToHHMM(from)}–${minutesToHHMM(to)}`)
        .join("; ")
      text = notice.dataset.textPeriods
        .replace("%{minutes}", String(placed))
        .replace("%{places}", places)
    } else {
      const keys = [...new Set(result.placements.map(([key]) => key))]
      const places = keys.map((key) => name(key)).join(", ")
      text = notice.dataset.textScalar
        .replace("%{minutes}", String(placed))
        .replace("%{places}", places)
    }
    notice.textContent = text
    notice.classList.remove("hidden")
  }

  // What this entry's shortfall means for the person filling the form:
  //
  //   ok         nothing missing
  //   raised     the form just topped the Pause up, mirroring what the save does
  //   pending    the save will correct it, but this form cannot show it happening
  //              (the Pause is derived from von/bis rows this controller must not
  //              write) — so say so rather than demand minutes nobody can enter
  //   shortfall  somebody has to act; #blocks decides whether that stops the save
  entryState(prefix, entryElement, target, entered, shortfall, required, lowering) {
    if (shortfall === 0) {
      if (target < entered && lowering) return "lowered"

      return this.showingOwnCorrection(prefix, entered, required) ?? "ok"
    }
    if (this.authorOwnsPeriods(entryElement)) return "shortfall"
    if (!this.serverFixesValue) return "shortfall"

    return this.scalarWritableValue ? "raised" : "pending"
  }

  // The author's own break minutes for this entry, seeded once and then kept in
  // step with what they type. The base entry gets the server's provenance; a
  // block multi_entry_controller cloned is a new entry, so whatever is in it is
  // the author's. Mirrors ConfigurableTimeSheet#manual_break_minutes: a save
  // that touches time_break restates the baseline, one that only moves the
  // window leaves it alone — which is what lets a top-up be taken back later.
  baselineFor(prefix, entered, entryElement) {
    if (!this._baseline.has(prefix)) {
      const prefilled = this.prefilledMinutesFor(prefix, entryElement)
      // A pre-filled Pause is the server's proposal, so none of it is the
      // author's — the same reading #break_rule_prefill applies on the way out.
      const seed = prefilled > 0 ? 0 : this.seedBaseline(prefix, entered)
      this._baseline.set(prefix, seed)
      // Seeded too, or the very next sync would read the entered total (top-up
      // included) as something the author had just typed.
      this._lastWritten.set(prefix, entered)
      this._prefillIntact.set(prefix, prefilled > 0 && entered === prefilled)
    } else if (entered !== this._lastWritten.get(prefix)) {
      this._baseline.set(prefix, entered)
      // Somebody typed a Pause, so there is a statement here now and the
      // pre-fill is spent — for the rest of the form's life, whatever the value
      // later happens to equal. Tracked as a flag rather than by comparing
      // against prefilledMinutes: this controller's own lowering changes the
      // value too, and that must not count as anyone having said anything.
      this._prefillIntact.set(prefix, false)
      // The Pause we wrote is no longer the one on screen, so stop recognising
      // it. Left standing, an author who types some other total and then returns
      // to exactly the value we once wrote would be taken for this controller
      // writing again, and their entry judged against the stale baseline — the
      // form would raise the Pause back over the total they just asked for.
      // null can never equal a parsed minute count.
      this._lastWritten.set(prefix, null)
    }

    return this._baseline.get(prefix)
  }

  // Tell the save which minutes this form treated as the author's own. It is not
  // derivable there: what gets submitted is the total this controller already
  // corrected, so a top-up and a hand-typed value arrive identical — see
  // ConfigurableTimeSheet#manual_break_minutes. The field is rendered per entry
  // by _break_rule_hints, and only where the form owns the scalar Pause, so a
  // missing one means there is nothing to declare.
  declareBaseline(prefix, minutes) {
    const input = this._form.querySelector(`input[name="${prefix}[manual_break_minutes]"]`)
    if (input) input.value = String(minutes)
  }

  // The base block's name prefix — the only entry the server shipped a baseline
  // for. Everything else in the form is a clone, and therefore new.
  basePrefix() {
    return "configurable_time_sheet"
  }

  seedBaseline(prefix, entered) {
    return prefix === this.basePrefix() ? this.manualMinutesValue : entered
  }

  // The Pause the SERVER put on this entry. The Schnellerfassung renders a row
  // per day, each with its own pre-filled Pause, so the row says so itself; the
  // single-entry forms carry one form-level value for their base block. A block
  // multi_entry_controller cloned has neither: it was copied from what was on
  // screen, which is a person's.
  prefilledMinutesFor(prefix, entryElement) {
    const own = entryElement?.dataset?.breakPrefilledMinutes
    if (own !== undefined) return parseInt(own, 10) || 0

    return prefix === this.basePrefix() ? this.prefilledMinutesValue : 0
  }

  // The total the save would leave on this entry: the requirement, never below
  // what the author entered themselves. Where nothing may be lowered the rule
  // only ever adds, so whatever is already on screen stands.
  targetBreakMinutes(manual, entered, required, lowering) {
    const target = Math.max(manual, required)

    return lowering ? target : Math.max(target, entered)
  }

  // Whether the Pause on screen is a raise this form made itself and nobody has
  // touched since. That value was never saved and never anyone's statement, so
  // the form may take it back down when the window shrinks again — whatever the
  // rule's lowering settings, which are about breaks already on the record.
  // Without this a passing keystroke fixes the Pause for good: typing "13:00"
  // reads "1" as 01:00, a 13-hour window raises the Pause to 00:45, and nothing
  // lowers it once the end is complete.
  ownUnsavedRaise(prefix, entered) {
    const correction = this._correctedTo.get(prefix)
    return (
      correction?.kind === "raised" &&
      correction.minutes === entered &&
      this._lastWritten.get(prefix) === entered
    )
  }

  // Lowering is the scalar path only — von/bis rows say WHEN a pause was taken
  // and the save never shrinks them, so the form must not either.
  //
  // Two separate permissions land here. "lower_to_minimum" is about the rule's
  // own earlier top-up and applies to any entry. "lower_prefilled_break" is
  // narrower and lives only in this form: the Pause on screen is still the one
  // the server proposed, so there is nothing of anyone's to protect yet.
  canLower(entryElement, prefix, rule) {
    if (!this.serverFixesValue || !this.scalarWritableValue || this.authorOwnsPeriods(entryElement))
      return false

    return rule.lowerToMinimum || this.loweringUntouchedPrefill(prefix, rule)
  }

  loweringUntouchedPrefill(prefix, rule) {
    return rule.lowerPrefilledBreak && this._prefillIntact.get(prefix) === true
  }

  // Whether the rule has anything to say about this entry at all. Two reasons it
  // has not, both only ever true in the Schnellerfassung: the row belongs to a
  // day the user unticked (carried along as entries[N][include]=0 and dropped by
  // the batch creator), or its own day resolves to no break rule — a work-time
  // model boundary inside the rendered week.
  skipEntry(entryElement) {
    if (entryElement?.dataset?.breakRuleSkip === "true") return true

    return entryElement?.querySelector("input[name$='[include]']")?.value === "0"
  }

  // The rule as it applies to ONE entry. Everything a work-time model decides per
  // DAY — the mode, its action, the thresholds and the two lowering permissions —
  // can differ between the rows of one form, because the Schnellerfassung's week
  // mode renders a row per day and a model boundary can fall between two of them.
  // A row whose day resolves to another rule carries it as JSON; every other form
  // holds exactly one entry-date, so the form-level Stimulus values are the rule.
  ruleFor(entryElement) {
    const base = {
      mode: this.modeValue,
      action: this.actionValue,
      atLeast: this.atLeastValue,
      lowerToMinimum: this.lowerToMinimumValue,
      lowerPrefilledBreak: this.lowerPrefilledBreakValue,
      thresholds: this.thresholdsValue,
    }
    const own = entryElement?.dataset?.breakRuleOverrides
    if (!own) return base

    try {
      return { ...base, ...JSON.parse(own) }
    } catch {
      return base
    }
  }

  hintMinutes(prefix, state, target, shortfall) {
    if (state === "raised") return this._correctedTo.get(prefix)?.minutes ?? target
    if (state === "lowered" || state === "pending") return target

    return shortfall
  }

  // The correction we made to this entry, while it is still the one on screen and
  // still worth explaining — "raised" or "lowered", or null for nothing to say.
  //
  // Forgotten as soon as the author edits the Pause themselves. A RAISE is also
  // forgotten once the day drops back under every threshold: the note explains a
  // requirement, and there is none left. A lowering is not, because it is at its
  // most relevant exactly then — that is the case it exists for.
  showingOwnCorrection(prefix, entered, required) {
    const correction = this._correctedTo.get(prefix)
    if (correction === undefined) return null
    if (entered !== correction.minutes || (correction.kind === "raised" && required === 0)) {
      this._correctedTo.delete(prefix)
      return null
    }

    return correction.kind
  }

  // Whether a surviving shortfall stops the save, mirroring
  // Timesheet::WorkTimeRuleChecker#configured_break_action: auto_deduct blocks only
  // where the form's author owns the von/bis periods (everywhere else it warns and
  // saves), validate uses its configured action. Both are moot where the save
  // cannot refuse the entry in the first place — see enforcedValue.
  blocks(entryElement, rule) {
    if (!this.enforcedValue) return false

    return rule.mode === "auto_deduct"
      ? this.authorOwnsPeriods(entryElement)
      : rule.action === "block"
  }

  // The ownership flag only bites once the entry actually HAS period rows — with
  // none, ConfigurableTimeSheet#break_periods_entered? is false and the save treats
  // the entry as scalar.
  authorOwnsPeriods(entryElement) {
    return this.authorOwnsPeriodsValue && this.liveBreakPeriodRows(entryElement).length > 0
  }

  // Rows the save will actually see. nested-form only HIDES a removed row and sets
  // _destroy=1, and break_periods_entered? rejects marked_for_destruction? — so
  // counting the markup would keep treating an entry as period-based after its last
  // row was deleted. Same two exclusions break_periods_total_controller applies.
  liveBreakPeriodRows(entryElement) {
    return [...entryElement.querySelectorAll("[data-break-period-row]")].filter((row) => {
      const destroy = row.querySelector("input[name*='_destroy']")
      if (destroy && destroy.value === "1") return false

      return row.offsetParent !== null
    })
  }

  // Required total break for the entered net minutes: the largest break_minutes
  // among the thresholds the day crosses. Whether landing exactly on one counts
  // is the rule's own setting. Mirrors Timesheet::BreakThresholds.
  requiredBreakMinutes(netMinutes, rule) {
    const netHours = netMinutes / 60
    return rule.thresholds.reduce((required, threshold) => {
      const after = Number(threshold.after_hours)
      const crossed = rule.atLeast ? after <= netHours : after < netHours

      return crossed ? Math.max(required, Number(threshold.break_minutes) || 0) : required
    }, 0)
  }

  // A stepper's hidden field and its visible display input share a name; the
  // hidden one is what submits, so it is the one to write.
  //
  // The written total is remembered so the next sync can tell it apart from a
  // value the author typed — that is the whole basis of the baseline.
  writeBreak(prefix, minutes) {
    const value = minutesToHHMM(minutes)
    this._lastWritten.set(prefix, minutes)

    const inputs = [...this._form.querySelectorAll(`input[name="${prefix}[time_break]"]`)]
    const input = inputs.find((i) => i.type === "hidden") || inputs[0]
    if (!input || input.value === value) return

    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Hints are resolved inside the entry rather than as Stimulus targets: this
  // controller sits on the form, and multi_entry_controller clones the entry — so
  // a target lookup would resolve every entry's hint to the first block's markup.
  renderHints(entryElement, state, minutes) {
    const shown = state === "ok" ? null : state
    ;["raised", "lowered", "pending", "shortfall"].forEach((name) => {
      const hint = entryElement.querySelector(`[data-break-rule-hint="${name}"]`)
      if (!hint) return

      hint.classList.toggle("hidden", name !== shown)
      if (name !== shown) return

      const slot = hint.querySelector("[data-break-rule-value]")
      if (slot) slot.textContent = name === "shortfall" ? String(minutes) : minutesToHHMM(minutes)
    })
  }
}
